import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { auth, database } from '../firebase/config';

const RegistrationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    referredBy: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Inline Styles
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '48px 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    card: {
      maxWidth: '480px',
      width: '100%',
      backgroundColor: '#ffffff',
      padding: '40px',
      borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '8px',
      letterSpacing: '-0.5px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#718096',
      marginTop: '8px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '4px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '14px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s',
      backgroundColor: '#ffffff',
      color: '#2d3748'
    },
    inputFocus: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
    },
    button: {
      width: '100%',
      padding: '14px 24px',
      fontSize: '16px',
      fontWeight: '600',
      color: '#ffffff',
      backgroundColor: '#667eea',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '8px'
    },
    buttonHover: {
      backgroundColor: '#5568d3',
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 20px rgba(102, 126, 234, 0.4)'
    },
    buttonDisabled: {
      backgroundColor: '#cbd5e0',
      cursor: 'not-allowed',
      transform: 'none'
    },
    alertContainer: {
      padding: '14px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginTop: '16px'
    },
    errorAlert: {
      backgroundColor: '#fff5f5',
      border: '1px solid #feb2b2',
      color: '#c53030'
    },
    successAlert: {
      backgroundColor: '#f0fff4',
      border: '1px solid #9ae6b4',
      color: '#2f855a'
    },
    icon: {
      flexShrink: 0,
      width: '20px',
      height: '20px'
    },
    alertText: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.5'
    },
    footer: {
      textAlign: 'center',
      marginTop: '24px',
      fontSize: '14px',
      color: '#718096'
    },
    link: {
      color: '#667eea',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'color 0.2s'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '50%',
      borderTopColor: '#ffffff',
      animation: 'spin 0.8s linear infinite',
      marginRight: '8px',
      verticalAlign: 'middle'
    }
  };

  // Default user data template
  const getDefaultUserData = (uid, name, email, mobile, referredBy) => {
    const currentTimestamp = Date.now();
    
    return {
      MySales: "0",
      MyTeam: "",
      Role: "Agency",
      aadhar: "123456789012",
      aadharCardVerifiedAt: currentTimestamp,
      address: "Gandhinagar ko",
      bankDetails: {
        accountHolderName: "Rachit",
        accountNumber: "12345678910",
        accountType: "savings",
        bankName: "Bob",
        branchName: "Gnr",
        ifscCode: "ABCD0123445",
        verified: true,
        verifiedAt: currentTimestamp
      },
      bankDetailsVerifiedAt: currentTimestamp,
      city: "North Goa",
      confirmedByTrainer: "ram shinde",
      createdAt: new Date().toISOString(),
      currentLevel: "AGENCY 15%",
      documentVerification: {
        completedSteps: 3,
        documents: {
          aadharCard: {
            docType: "aadharCard",
            downloadURL: "https://firebasestorage.googleapis.com/v0/b/company-2f343.firebasestorage.app/o/document_verification%2Fpan%20card.jpg?alt=media&token=e254d54b-f3f8-4039-b977-b1d545806495",
            fileName: "IMG-20251102-WA0015.jpg",
            fileSize: 69651,
            fileType: "image/jpeg",
            formData: {
              aadharNumber: "1234 5678 9012",
              docType: "aadharCard",
              nameOnAadhar: "Rachit",
              uploadedAt: new Date().toISOString()
            },
            reviewedAt: currentTimestamp,
            reviewedBy: "admin",
            sanitizedFileName: "IMG-20251102-WA0015.jpg",
            status: "approved",
            storagePath: `document_verification/${uid}/aadharCard_${uid}_${currentTimestamp}.jpg`,
            uploadedAt: new Date().toISOString(),
            userId: uid,
            verificationStatus: "approved"
          },
          bankDetails: {
            docType: "bankDetails",
            downloadURL: "https://firebasestorage.googleapis.com/v0/b/company-2f343.firebasestorage.app/o/document_verification%2Fpassbook%20elite.jpg?alt=media&token=8de1171b-2092-41b4-9c5b-1bf51c664532",
            fileName: "IMG-20251102-WA0015.jpg",
            fileSize: 69651,
            fileType: "image/jpeg",
            formData: {
              accountHolderName: "Rachit",
              accountNumber: "12345678910",
              accountType: "savings",
              bankName: "Bob",
              branchName: "Gnr",
              docType: "bankDetails",
              ifscCode: "ABCD0123445",
              uploadedAt: new Date().toISOString()
            },
            reviewedAt: currentTimestamp,
            reviewedBy: "admin",
            sanitizedFileName: "IMG-20251102-WA0015.jpg",
            status: "approved",
            storagePath: `document_verification/${uid}/bankDetails_${uid}_${currentTimestamp}.jpg`,
            uploadedAt: new Date().toISOString(),
            userId: uid,
            verificationStatus: "approved"
          },
          panCard: {
            docType: "panCard",
            downloadURL: "https://firebasestorage.googleapis.com/v0/b/company-2f343.firebasestorage.app/o/document_verification%2Fvijay_image_src.jpg?alt=media&token=69e73741-3a29-4fe9-bf32-8d9217df79d9",
            fileName: "IMG-20251102-WA0015.jpg",
            fileSize: 69651,
            fileType: "image/jpeg",
            formData: {
              docType: "panCard",
              nameOnPan: "Rachit",
              panNumber: "ABCDE1234E",
              uploadedAt: new Date().toISOString()
            },
            reviewedAt: currentTimestamp,
            reviewedBy: "admin",
            sanitizedFileName: "IMG-20251102-WA0015.jpg",
            status: "approved",
            storagePath: `document_verification/${uid}/panCard_${uid}_${currentTimestamp}.jpg`,
            uploadedAt: new Date().toISOString(),
            userId: uid,
            verificationStatus: "approved"
          }
        },
        finalReviewAt: currentTimestamp,
        lastUpdated: new Date().toISOString(),
        overallStatus: "approved",
        reviewedBy: "admin",
        submittedAt: new Date().toISOString(),
        totalDocuments: 3,
        userDetails: {
          currentLevel: "AGENCY 15%",
          email: email,
          name: name,
          phone: mobile,
          role: "agency",
          userId: uid
        }
      },
      documentVerificationCompletedAt: currentTimestamp,
      documentVerificationStatus: "approved",
      email: email,
      firstTime: false,
      isActive: true,
      joinedViaTraining: "-OcslJgykuNa8NkcaWEF",
      lastUpdated: currentTimestamp,
      mobile: mobile,
      name: name,
      pan: "ABCDE1234E",
      panCardVerifiedAt: currentTimestamp,
      phone: mobile,
      pin: "416119",
      referredBy: referredBy,
      role: "agency",
      state: "Goa"
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!formData.name || !formData.email || !formData.mobile || !formData.password) {
        throw new Error('All fields except Referred By are required');
      }

      if (formData.mobile.length !== 10) {
        throw new Error('Mobile number must be 10 digits');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      const uid = user.uid;

      const userData = getDefaultUserData(
        uid,
        formData.name,
        formData.email,
        formData.mobile,
        formData.referredBy || ""
      );

      const userRef = ref(database, `HTAMS/users/${uid}`);
      await set(userRef, userData);

      setSuccess('Registration successful! Redirecting to dashboard...');
      
      setFormData({
        name: '',
        email: '',
        mobile: '',
        password: '',
        referredBy: ''
      });

      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);

    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          input:focus {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
          }
          
          button:hover:not(:disabled) {
            background-color: #5568d3 !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 10px 20px rgba(102, 126, 234, 0.4) !important;
          }
          
          a:hover {
            color: #5568d3 !important;
          }
        `}
      </style>

      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Create Your Account</h2>
            <p style={styles.subtitle}>Join HTAMS Agency Network</p>
          </div>

          <form style={styles.form} onSubmit={handleSubmit}>
            {/* Name Input */}
            <div style={styles.inputGroup}>
              <label htmlFor="name" style={styles.label}>
                Full Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter your full name"
              />
            </div>

            {/* Email Input */}
            <div style={styles.inputGroup}>
              <label htmlFor="email" style={styles.label}>
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                placeholder="your.email@example.com"
              />
            </div>

            {/* Mobile Input */}
            <div style={styles.inputGroup}>
              <label htmlFor="mobile" style={styles.label}>
                Mobile Number *
              </label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                required
                value={formData.mobile}
                onChange={handleChange}
                maxLength="10"
                pattern="[0-9]{10}"
                style={styles.input}
                placeholder="10-digit mobile number"
              />
            </div>

            {/* Password Input */}
            <div style={styles.inputGroup}>
              <label htmlFor="password" style={styles.label}>
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                placeholder="Minimum 6 characters"
              />
            </div>

            {/* Referred By Input */}
            <div style={styles.inputGroup}>
              <label htmlFor="referredBy" style={styles.label}>
                Referred By (User ID)
              </label>
              <input
                id="referredBy"
                name="referredBy"
                type="text"
                value={formData.referredBy}
                onChange={handleChange}
                style={styles.input}
                placeholder="Referrer's User ID (Optional)"
              />
            </div>

            {/* Error Alert */}
            {error && (
              <div style={{ ...styles.alertContainer, ...styles.errorAlert }}>
                <svg style={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div style={styles.alertText}>{error}</div>
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div style={{ ...styles.alertContainer, ...styles.successAlert }}>
                <svg style={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div style={styles.alertText}>{success}</div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={loading ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
            >
              {loading ? (
                <span>
                  <span style={styles.spinner}></span>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Login Link */}
            <div style={styles.footer}>
              <p>
                Already have an account?{' '}
                <a href="/login" style={styles.link}>
                  Sign in here
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RegistrationPage;
